import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "@/i18n/I18nProvider";
import { cn } from "@/lib/cn";
import { SPRING_PILL } from "@/lib/motion";

export interface LanguageToggleProps {
  className?: string;
  /**
   * Render in normal flow instead of fixed to the top-right corner — used
   * inside the account sidebar's Settings section. Default (false) keeps the
   * original floating placement for the consumer pages.
   */
  inline?: boolean;
}

/**
 * Two-state pill (EN | RO) with an animated thumb that slides under the
 * active label. By default fixed to the top-right corner, sitting behind the
 * floor-plan modal (z-50 vs the modal's z-100) so it doesn't compete for the
 * tap surface while a venue is open; `inline` drops the positioning entirely.
 *
 * The fixed variant's `top` uses `max(spacing, env(safe-area-inset-top))` so
 * it sits just below the iPhone notch when Safari is in immersive mode, and
 * at a normal 12px offset otherwise.
 */
export function LanguageToggle({ className, inline }: LanguageToggleProps) {
  const { lang, setLang, t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const isEN = lang === "en";

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full border border-white/15 light:border-black/15 bg-zinc-950/90 light:bg-white/90 p-1 shadow-[0_0_0_1px_rgba(0,0,0,0.7)] light:shadow-none backdrop-blur",
        !inline && "fixed right-3 z-50 sm:right-5",
        className,
      )}
      style={inline ? undefined : { top: "max(0.75rem, env(safe-area-inset-top, 0px))" }}
      role="group"
      aria-label="Language selector"
    >
      <div className="relative flex">
        {/* Animated thumb — sits under the active label */}
        <motion.div
          aria-hidden
          animate={{ x: isEN ? "0%" : "100%" }}
          transition={reduceMotion ? { duration: 0 } : SPRING_PILL}
          className="pointer-events-none absolute inset-y-0 left-0 w-1/2 rounded-full bg-paper light:bg-ink"
        />
        <button
          type="button"
          onClick={() => setLang("en")}
          aria-pressed={isEN}
          aria-label={t("LANG_TOGGLE_TO_EN")}
          className={cn(
            "relative z-10 min-w-[44px] rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors sm:px-4 sm:py-1.5",
            isEN ? "text-zinc-900 light:text-zinc-50" : "text-zinc-400 light:text-zinc-600 hover:text-zinc-200 light:hover:text-zinc-800",
          )}
        >
          EN
        </button>
        <button
          type="button"
          onClick={() => setLang("ro")}
          aria-pressed={!isEN}
          aria-label={t("LANG_TOGGLE_TO_RO")}
          className={cn(
            "relative z-10 min-w-[44px] rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors sm:px-4 sm:py-1.5",
            !isEN ? "text-zinc-900 light:text-zinc-50" : "text-zinc-400 light:text-zinc-600 hover:text-zinc-200 light:hover:text-zinc-800",
          )}
        >
          RO
        </button>
      </div>
    </div>
  );
}
